import { useState, useEffect } from "react";
import { useAuth } from "@/components/ProtectedRoute";
import api, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { User, Mail, Camera, Lock, Eye, EyeOff, Loader2, Check, Upload, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const API = `${getApiUrl()}/api`;

export default function AccountSettings() {
  const { user, refreshUser } = useAuth();
  
  // Profile state
  const [name, setName] = useState("");
  const [pictureUrl, setPictureUrl] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Username state
  const [username, setUsername] = useState("");
  const [usernameLoading, setUsernameLoading] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPictureUrl(user.picture || "");
      setUsername(user.username || "");
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const response = await api.put(`${API}/auth/account`, {
        name: name,
        picture: pictureUrl || null
      });

      toast.success("Profile updated successfully!");
      
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update profile";
      toast.error(message);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Username cannot be empty");
      return;
    }
    setUsernameLoading(true);
    try {
      await api.put(`${API}/auth/username`, { username: username.trim() });
      toast.success("Username updated successfully!");
      if (refreshUser) await refreshUser();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update username";
      toast.error(message);
    } finally {
      setUsernameLoading(false);
    }
  };


  const getPasswordStrength = () => {
    if (!newPassword) return 0;
    let strength = 0;
    if (newPassword.length >= 8) strength++;
    if (/[A-Z]/.test(newPassword)) strength++;
    if (/[0-9]/.test(newPassword)) strength++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    if (passwordStrength < 4) {
      toast.error("New password doesn't meet requirements");
      return;
    }

    setPasswordLoading(true);

    try {
      await api.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="account-settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
          Account Settings
        </h1>
        <p className="text-white/50 mt-1">Manage your profile and security settings</p>
      </div>

      {/* Profile Section */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <User className="w-5 h-5 text-purple-400" />
            Profile Information
          </CardTitle>
          <CardDescription className="text-white/50">
            Update your display name and profile picture
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            {/* Profile Picture Preview */}
            <div className="flex items-center gap-6">
              <div className="relative">
                {pictureUrl ? (
                  <img 
                    src={pictureUrl} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-white/10"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-20 h-20 rounded-full bg-purple-600 items-center justify-center text-white text-2xl font-bold ${pictureUrl ? 'hidden' : 'flex'}`}
                >
                  {name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
                  <Camera className="w-3.5 h-3.5 text-white/50" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{name || "Your Name"}</p>
                <p className="text-white/40 text-sm">{user?.email}</p>
              </div>
            </div>

            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">Display Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your display name"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="account-name-input"
              />
            </div>

            {/* Picture URL Input */}
            <div className="space-y-2">
              <Label htmlFor="picture" className="text-white/70">Profile Picture URL</Label>
              <div className="relative">
                <Upload className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="picture"
                  type="url"
                  value={pictureUrl}
                  onChange={(e) => setPictureUrl(e.target.value)}
                  placeholder="https://example.com/your-photo.jpg"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="account-picture-input"
                />
              </div>
              <p className="text-white/40 text-xs">Enter a URL to an image (JPG, PNG, etc.)</p>
            </div>

            <Button
              type="submit"
              disabled={profileLoading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="account-save-profile"
            >
              {profileLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Public Username Section */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AtSign className="w-5 h-5 text-purple-400" />
            Public Username
          </CardTitle>
          <CardDescription className="text-white/50">
            This username is displayed on your signature cards instead of your real name
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateUsername} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/70">Username</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. MrAspirin"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="account-username-input"
                  maxLength={20}
                />
              </div>
              <p className="text-white/40 text-xs">3-20 characters: letters, numbers, underscores, hyphens. Must be unique.</p>
            </div>
            <Button
              type="submit"
              disabled={usernameLoading || !username.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="account-save-username"
            >
              {usernameLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Username
            </Button>
          </form>
        </CardContent>
      </Card>


      {/* Password Section */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-purple-400" />
            Change Password
          </CardTitle>
          <CardDescription className="text-white/50">
            Update your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-white/70">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="currentPassword"
                  type={showPasswords ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="account-current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Separator className="bg-white/10" />

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-white/70">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="newPassword"
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  data-testid="account-new-password"
                />
              </div>
              
              {/* Password strength indicator */}
              {newPassword && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordStrength >= level
                            ? level <= 1 ? "bg-red-500"
                            : level <= 2 ? "bg-yellow-500"
                            : level <= 3 ? "bg-blue-500"
                            : "bg-green-500"
                            : "bg-white/10"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-white/40">
                    {passwordStrength < 4 && (
                      <>
                        Needs: {newPassword.length < 8 && "8+ chars, "}
                        {!/[A-Z]/.test(newPassword) && "uppercase, "}
                        {!/[0-9]/.test(newPassword) && "number, "}
                        {!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) && "special char"}
                      </>
                    )}
                    {passwordStrength === 4 && <span className="text-green-400">Strong password!</span>}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/70">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="confirmPassword"
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className={`pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 ${
                    confirmPassword && newPassword !== confirmPassword ? "border-red-500/50" : ""
                  }`}
                  data-testid="account-confirm-password"
                />
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400">Passwords don't match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={passwordLoading || passwordStrength < 4 || newPassword !== confirmPassword || !currentPassword}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="account-change-password"
            >
              {passwordLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-400" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center py-2">
            <div>
              <p className="text-white/70 text-sm">Email Address</p>
              <p className="text-white">{user?.email}</p>
            </div>
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Verified</span>
          </div>
          <Separator className="bg-white/10" />
          <div className="flex justify-between items-center py-2">
            <div>
              <p className="text-white/70 text-sm">Account Type</p>
              <p className="text-white">{user?.isAdmin ? "Administrator" : "Standard User"}</p>
            </div>
            {user?.isAdmin && (
              <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">Admin</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
